import { useState, useEffect, useCallback } from "react";
import {
  IPerformanceMetricCompetencies,
  UserInterface,
  ICountry,
} from "../../../interface";
import CountryFilter from "../../../components/CountryFilter";
import { Category } from "../../../interface/Enums/category.enum";
import { PerfType } from "../../../interface/Enums/peftype.enum";
import { useDispatch, useSelector } from "react-redux";
import { getAllCountries } from "../../../APIs";
import { fetchAPI } from "../../../utils/FetchApi";
import { FetchMethods } from "../../../interface/Enums/methods.enum";
import { setError } from "../../../store/reducers/errorReducer";
import Dropdown from "react-bootstrap/Dropdown";
import { BsThreeDotsVertical } from "react-icons/bs";
import Badge from "react-bootstrap/Badge";
import Accordion from "react-bootstrap/Accordion";
import { BsPerson } from "react-icons/bs";
import AdminContainer from "../../../admin-components/AdminContainer";
import { Modal, BtnCreate } from "../../../components/StyledComponents";
const ManagePerformanceCompetenciesMetric = () => {
  const dispatch: any = useDispatch();
  const data: UserInterface = useSelector((data: any) => {
    return data.auth.user;
  });

  const [listOfCountries, setlistOfCountries] = useState<ICountry[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [performanceMetrics, setPerformanceMetrics] = useState<
    IPerformanceMetricCompetencies[]
  >([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [formData, setFormData] = useState<IPerformanceMetricCompetencies>({
    name: "",
    explanation: "",
    category: Category.COMPETENCIES,
    type: PerfType.STANDARD,
    country: "",
    createdAt: new Date(),
    updatedAt: new Date(),
    creator: data?.name && data?.surname ? data.name + " " + data.surname : "",
    _id: "",
  });
  const [makeReload, setMakeReload] = useState<boolean>(false);

  const resetForm = () => {
    setFormData({
      name: "",
      explanation: "",
      category: Category.COMPETENCIES,
      type: PerfType.STANDARD,
      country: "",
      createdAt: new Date(),
      updatedAt: new Date(),
      creator:
        data?.name && data?.surname ? data.name + " " + data.surname : "",
      _id: "",
    });
  };

  const openCreateModal = () => {
    const currentCreator =
      data?.name && data?.surname ? data.name + " " + data.surname : "";
    setFormData({
      name: "",
      explanation: "",
      category: Category.COMPETENCIES,
      type: PerfType.STANDARD,
      country: "",
      createdAt: new Date(),
      updatedAt: new Date(),
      creator: currentCreator,
      _id: "",
    });
    setIsEditMode(false);
    setShowModal(true);
  };

  const openEditModal = (metric: IPerformanceMetricCompetencies) => {
    setFormData(metric);
    setIsEditMode(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    resetForm();
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (
      formData.name.length === 0 ||
      formData.explanation.length === 0 ||
      formData.country.length === 0
    ) {
      dispatch(setError("Name, explanation and country are required"));
      return;
    }

    try {
      if (isEditMode) {
        // For update, send the full formData including _id
        await fetchAPI(
          "/admincmds/update-performance-competencies-metric",
          FetchMethods.PATCH,
          formData
        );
      } else {
        // For create, only send the necessary fields (exclude _id, createdAt, updatedAt)
        const createData = {
          name: formData.name,
          explanation: formData.explanation,
          category: formData.category,
          type: formData.type,
          country: formData.country,
          creator: formData.creator,
        };
        await fetchAPI(
          "/admincmds/create-performance-competencies-metric",
          FetchMethods.POST,
          createData
        );
      }

      setMakeReload(!makeReload);
      closeModal();
    } catch (error: any) {
      dispatch(setError(error.message?.toString() || error.toString()));
    }
  };

  const deletePerformanceMetric = async (index: number) => {
    //confirm delete
    if (
      !window.confirm(
        "Are you sure you want to delete this performance metric?"
      )
    )
      return;

    try {
      const deletedInput: IPerformanceMetricCompetencies = await fetchAPI(
        `/admincmds/delete-performance-competencies-metric/${performanceMetrics[index]._id}`,
        FetchMethods.GET
      );

      if (deletedInput.hasOwnProperty("error")) {
        //@ts-ignore
        throw new Error(deletedInput.message?.toString());
      }

      setMakeReload(!makeReload);
    } catch (error: any) {
      dispatch(setError(error.toString()));
    }
  };

  const pickCountry = useCallback((country: string): void => {
    setSelectedCountry(country);
  }, []);

  /**
   * Use Effects
   */
  useEffect(() => {
    (async () => {
      const ctries: ICountry[] = await getAllCountries();
      ctries.sort((a, b) => (a.name > b.name ? 1 : -1));
      setlistOfCountries(ctries);
    })();
  }, []);

  //Load all inputs based on the country
  useEffect(() => {
    if (selectedCountry.length > 0) {
      (async () => {
        try {
          console.log(selectedCountry);
          const getAllInputs: IPerformanceMetricCompetencies[] = await fetchAPI(
            `/admincmds/get-all-performance-competencies-metrics/${selectedCountry}`,
            FetchMethods.GET
          );
          console.log(getAllInputs);
          if (getAllInputs.hasOwnProperty("error")) {
            throw new Error(JSON.stringify(getAllInputs));
          }
          setPerformanceMetrics(getAllInputs);
        } catch (error: any) {
          dispatch(JSON.stringify(error.message));
        }
      })();
    }

    //do nothing yet
  }, [selectedCountry, dispatch, makeReload]);

  return (
    <AdminContainer>
      <div className="row justify-content-between align-items-center">
        <div className="col-lg-8 m-2">
          <CountryFilter returnedCountry={pickCountry} />
        </div>
        <div className="col-lg-2 m-2">
          <BtnCreate onClick={openCreateModal}>
            Create Performance Competency Metric
          </BtnCreate>
        </div>
      </div>
      <hr className="hr" />
      <div className="row justify-content-md-center">
        {performanceMetrics.length > 0 &&
          performanceMetrics.map(
            (c: IPerformanceMetricCompetencies, i: number) => (
              <div className="card col-lg-5 m-2 p-4 shadow rounded" key={i}>
                <div className="row">
                  <div className="col-lg-2">
                    <Dropdown>
                      <Dropdown.Toggle size="sm" variant="info">
                        <BsThreeDotsVertical />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          href="#/action-1"
                          onClick={() => {
                            openEditModal(performanceMetrics[i]);
                          }}
                        >
                          Update
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#/action-2"
                          onClick={() => {
                            deletePerformanceMetric(i);
                          }}
                        >
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="col-lg-10">
                    <Badge
                      bg={c.category === "input" ? "secondary" : "warning"}
                      className="mr-2"
                    >
                      {c.category}
                    </Badge>
                    <Accordion>
                      <Accordion.Item eventKey={i.toString()}>
                        <Accordion.Header>
                          <BsPerson />
                          {"   "}
                          <small className="m-2" style={{ fontWeight: "bold" }}>
                            {c.name}
                          </small>
                        </Accordion.Header>
                        <Accordion.Body key={i}>
                          <strong>Explanation:</strong>
                          <p>{c.explanation}</p>
                          <br />
                          <Badge bg="secondary">By: {c.creator}</Badge>
                          <br />
                          <Badge bg="secondary">
                            Country scope: {c.country}
                          </Badge>
                          <br />
                          <Badge bg="secondary">Category: {c.category}</Badge>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                </div>
              </div>
            )
          )}
      </div>

      {/* Modal for Create/Update */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={
          isEditMode
            ? "Update Performance Competency Metric"
            : "Create Performance Competency Metric"
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group mb-3">
            <label htmlFor="name" className="form-label">
              Name
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group mb-3">
            <label htmlFor="explanation" className="form-label">
              Description
            </label>
            <textarea
              className="form-control"
              id="explanation"
              name="explanation"
              value={formData.explanation}
              onChange={handleInputChange}
              rows={3}
              required
            />
          </div>

          <div className="form-group mb-3">
            <label htmlFor="country" className="form-label">
              Country
            </label>
            <select
              className="form-control"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              required
            >
              <option value="">Choose country</option>
              {listOfCountries.map((country: ICountry, index: number) => (
                <option key={index} value={country.name}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group mb-3">
            <label htmlFor="creator" className="form-label">
              Creator Information
            </label>
            <input
              type="text"
              className="form-control"
              id="creator"
              name="creator"
              value={formData.creator}
              disabled
            />
          </div>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <button
              type="button"
              className="dai-btn dai-btn-secondary"
              onClick={closeModal}
            >
              Cancel
            </button>
            <button type="submit" className="dai-btn dai-btn-success">
              {isEditMode ? "Update" : "Create"} Performance Competency Metric
            </button>
          </div>
        </form>
      </Modal>
    </AdminContainer>
  );
};

export default ManagePerformanceCompetenciesMetric;
